import { type LucideIcon } from 'lucide-react'
import { cn } from '../lib/utils'

/**
 * CheckCardGroup — выбор НЕСКОЛЬКИХ значений карточками в сетке.
 * Множественный близнец <RadioCardGroup>: та же лестница elevation и та же сетка,
 * отличается только семантикой (role="checkbox", value — массив).
 * Нужен там, где сохраняемая настройка допускает несколько значений (категории курса):
 * <Chip> для этого нельзя — он канон ФИЛЬТРОВ, а не фиксируемого выбора.
 */
export interface CheckCardOption {
  id: string
  label: string
  icon?: LucideIcon
}

interface CheckCardGroupProps {
  options: CheckCardOption[]
  value: string[]
  onChange: (next: string[]) => void
  columns?: 1 | 2 | 3
  className?: string
  /**
   * Блокировка настоящая: native disabled на кнопках + aria-disabled на группе.
   * Гасить снаружи одним pointer-events-none нельзя — Tab+Space остаётся рабочим.
   * Визуал в состоянии покоя байт-в-байт как у близнеца <RadioCardGroup>.
   */
  disabled?: boolean
}

const colsClass: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
}

export function CheckCardGroup({
  options,
  value,
  onChange,
  columns = 2,
  className,
  disabled = false,
}: CheckCardGroupProps) {
  return (
    <div
      role="group"
      aria-disabled={disabled || undefined}
      className={cn('grid gap-2', colsClass[columns], className)}
    >
      {options.map((opt) => {
        const Icon = opt.icon
        const selected = value.includes(opt.id)
        return (
          <button
            key={opt.id}
            type="button"
            role="checkbox"
            aria-checked={selected}
            disabled={disabled}
            onClick={() =>
              onChange(selected ? value.filter((id) => id !== opt.id) : [...value, opt.id])
            }
            className={cn(
              'flex items-center gap-2 p-2.5 rounded-2xl border-2 text-sm font-medium text-left transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
              'disabled:cursor-not-allowed disabled:opacity-50',
              selected
                ? 'border-foreground/40 bg-muted text-foreground'
                : 'border-border bg-card text-muted-foreground',
              // Ховер/нажатие только у живой карточки: :hover матчится и на disabled-кнопке,
              // иначе заблокированная карточка подсвечивалась бы как кликабельная.
              !selected && !disabled
                ? 'hover:bg-popover hover:border-foreground/20 hover:text-foreground active:bg-muted'
                : null,
            )}
          >
            {Icon ? <Icon className="w-4 h-4 shrink-0" strokeWidth={1.5} /> : null}
            <span className="truncate">{opt.label}</span>
          </button>
        )
      })}
    </div>
  )
}
